import { useState } from "react";
import { calcBMR, calcMaintenance, calcLBM, getCurrentBodyComposition } from "../utils/calculations";
import { btn, inp } from "../styles/shared";

const STAT = ({ label, value, sub, accent }) => (
  <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, padding: "14px 18px" }}>
    <div style={{ fontSize: 11, color: "#475569", fontFamily: "'Space Mono',monospace", marginBottom: 6, letterSpacing: 1 }}>{label.toUpperCase()}</div>
    <div style={{ fontSize: 22, fontWeight: 700, color: accent || "#f1f5f9", fontFamily: "'Space Mono',monospace" }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: "#475569", marginTop: 4 }}>{sub}</div>}
  </div>
);

export default function Profile({ fitnessGoals, saveFitnessGoals, macroGoals, saveMacroGoals, weightLog = [], showToast }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);

  function startEdit() {
    setDraft({
      name:    fitnessGoals.name    || "Jaleel",
      age:     fitnessGoals.age     || 32,
      height:  fitnessGoals.height  || 175,
      neck:    fitnessGoals.neck    || 0,
      waist:   fitnessGoals.waist   || 0,
    });
    setEditing(true);
  }

  function saveEdit() {
    const numberOrPrevious = (value, previous) => {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : previous;
    };
    saveFitnessGoals({
      ...fitnessGoals,
      name:   draft.name,
      age:    numberOrPrevious(draft.age, fitnessGoals.age),
      height: numberOrPrevious(draft.height, fitnessGoals.height),
      neck:   numberOrPrevious(draft.neck, fitnessGoals.neck),
      waist:  numberOrPrevious(draft.waist, fitnessGoals.waist),
    });
    setEditing(false);
    showToast("Profile updated");
  }

  const { weightCurrent: w, height: h, age } = fitnessGoals;
  const name = fitnessGoals.name || "Jaleel";

  // Latest weight from weightLog if available
  const sortedWeights = [...weightLog].sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
  const latestWeight = sortedWeights.length > 0
    ? sortedWeights[sortedWeights.length - 1].weight
    : w;

  const composition = getCurrentBodyComposition(fitnessGoals, weightLog);
  const bf = composition.bodyFat ?? fitnessGoals.bodyFatCurrent ?? null;
  const lbm = composition.leanMass ?? calcLBM(latestWeight, h);
  const baseline = composition.baseline;
  const bmr = calcBMR(latestWeight, h, age);
  const maint = calcMaintenance(latestWeight, h, age);
  const bfSource = baseline?.source === "navy" ? "navy + weight adjusted" : baseline?.source === "manual" ? "manual + weight adjusted" : "boer estimate";
  const lbmSource = bf != null ? "body-fat anchor" : "boer estimate";

  const section = { background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 16, padding: "20px 22px" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Header card */}
      <div style={{ ...section, display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
        {/* Avatar */}
        <div style={{
          width: 72, height: 72, borderRadius: "50%",
          background: "linear-gradient(135deg, #f97316, #a78bfa)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 28, fontWeight: 700, color: "#fff", flexShrink: 0,
          fontFamily: "'Space Mono',monospace",
        }}>
          {name.charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: "#f1f5f9", marginBottom: 4 }}>{name}</h1>
          <p style={{ fontSize: 13, color: "#64748b" }}>
            {age ? `${age} yrs` : ""}{age && h ? " · " : ""}{h ? `${h}cm` : ""}
            {latestWeight ? ` · ${latestWeight}kg` : ""}
          </p>
        </div>
        {!editing && (
          <button onClick={startEdit} style={{ ...btn, background: "rgba(255,255,255,0.05)", color: "#94a3b8", border: "1px solid rgba(255,255,255,0.1)", padding: "8px 18px", fontSize: 13, borderRadius: 10 }}>
            Edit
          </button>
        )}
      </div>

      {/* Edit form */}
      {editing && draft && (
        <div style={section}>
          <h3 style={{ fontSize: 14, color: "#94a3b8", marginBottom: 16, fontFamily: "'Space Mono',monospace", letterSpacing: 1 }}>EDIT PROFILE</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
            {[
              { key: "name",   label: "Name",         type: "text" },
              { key: "age",    label: "Age (yrs)",    type: "number" },
              { key: "height", label: "Height (cm)",  type: "number" },
              { key: "neck",   label: "Neck (cm)",    type: "number" },
              { key: "waist",  label: "Waist at navel (cm)", type: "number" },
            ].map(({ key, label, type }) => (
              <div key={key}>
                <div style={{ fontSize: 11, color: "#475569", marginBottom: 5 }}>{label}</div>
                <input
                  style={{ ...inp }}
                  type={type}
                  value={draft[key]}
                  onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))}
                />
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button onClick={saveEdit} style={{ ...btn, background: "rgba(249,115,22,0.15)", color: "#f97316", border: "1px solid rgba(249,115,22,0.3)", padding: "8px 20px", fontSize: 13, borderRadius: 10 }}>Save</button>
            <button onClick={() => setEditing(false)} style={{ ...btn, background: "transparent", color: "#64748b", border: "1px solid rgba(255,255,255,0.08)", padding: "8px 20px", fontSize: 13, borderRadius: 10 }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Body stats */}
      <div>
        <h3 style={{ fontSize: 12, color: "#475569", fontFamily: "'Space Mono',monospace", letterSpacing: 1, marginBottom: 12 }}>BODY STATS</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 12 }}>
          <STAT label="Weight"     value={latestWeight ? latestWeight + "kg" : "—"} sub="current" />
          <STAT label="Target"     value={fitnessGoals.weightTarget ? fitnessGoals.weightTarget + "kg" : "—"} sub="goal" />
          <STAT label="Body Fat"   value={bf != null ? bf + "%" : "—"} sub={bfSource} accent={bf != null && bf > 20 ? "#f97316" : "#22c55e"} />
          <STAT label="BF Target"  value={fitnessGoals.bodyFatTarget ? fitnessGoals.bodyFatTarget + "%" : "—"} sub="goal" />
          <STAT label="LBM"        value={lbm != null ? lbm.toFixed(1) + "kg" : "—"} sub={lbmSource} accent="#a78bfa" />
          <STAT label="BMR"        value={bmr ? bmr + " kcal" : "—"} sub="at rest" />
          <STAT label="Maintenance" value={maint ? maint + " kcal" : "—"} sub="sedentary TDEE" />
          <STAT label="Height"     value={h ? h + " cm" : "—"} sub="static" />
        </div>
      </div>

      {/* Macro targets */}
      <div>
        <h3 style={{ fontSize: 12, color: "#475569", fontFamily: "'Space Mono',monospace", letterSpacing: 1, marginBottom: 12 }}>DAILY MACRO TARGETS</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 12 }}>
          <STAT label="Calories" value={macroGoals.calories || "—"} sub="kcal / day" accent="#f97316" />
          <STAT label="Protein"  value={macroGoals.protein  ? macroGoals.protein + "g"  : "—"} sub="per day" accent="#f97316" />
          <STAT label="Carbs"    value={macroGoals.carbs    ? macroGoals.carbs    + "g"  : "—"} sub="per day" />
          <STAT label="Fat"      value={macroGoals.fat      ? macroGoals.fat      + "g"  : "—"} sub="per day" />
          <STAT label="Fiber"    value={macroGoals.fiber    ? macroGoals.fiber    + "g"  : "—"} sub="per day" />
        </div>
      </div>

      {/* Body composition targets */}
      <div>
        <h3 style={{ fontSize: 12, color: "#475569", fontFamily: "'Space Mono',monospace", letterSpacing: 1, marginBottom: 12 }}>COMPOSITION TARGETS</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 12 }}>
          <STAT label="Muscle" value={fitnessGoals.muscleCurrent ? fitnessGoals.muscleCurrent + "%" : "—"} sub="current %" />
          <STAT label="Muscle Target" value={fitnessGoals.muscleTarget ? fitnessGoals.muscleTarget + "%" : "—"} sub="goal %" />
          <STAT label="Run Dist" value={fitnessGoals.runDistTarget ? fitnessGoals.runDistTarget + " km" : "—"} sub="target" />
          <STAT label="Run Pace" value={fitnessGoals.runPaceTarget ? fitnessGoals.runPaceTarget + " min/km" : "—"} sub="target" />
        </div>
      </div>
    </div>
  );
}
